import { ReactElement, useEffect, useRef, useCallback } from 'react';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { getAuthorizationToken } from '../../config/authConfig';
import { getAppConfig } from '../../config/config';
import { Calculation, CalculationStatus } from 'types/domainTypes';

export function mapCompletionStatusToCalculationStatus(status: string): CalculationStatus {
  switch (status) {
    case 'CompletedPassed':
      return CalculationStatus.Pass;
    case 'CompletedFailed':
      return CalculationStatus.Fail;
    case 'Calculating':
      return CalculationStatus.Calculating;
    case 'FailedToCalculate':
      return CalculationStatus.FailedToCalculate;
    default:
      return CalculationStatus.InProgress;
  }
}

function updateArrayElement(
  id: string,
  status: CalculationStatus,
  calculations: Calculation[],
  setCalculations: (value: Calculation[]) => void
) {
  const index = calculations.findIndex((calculation) => calculation.id === id);
  if (index >= 0) {
    const updatedValues = [...calculations];
    updatedValues[index] = {
      ...calculations[index],
      status,
    };
    setCalculations(updatedValues);
  }
}

function updateCalculation<T extends Calculation | Calculation[]>(
  id: string,
  status: CalculationStatus,
  calculations: T,
  setCalculations: (value: T) => void,
  refetchCalculation?: () => void
) {
  if (calculations instanceof Array) {
    updateArrayElement(id, status, calculations, setCalculations as (value: Calculation[]) => void);
  } else if (calculations.id === id) {
    if (refetchCalculation && status !== CalculationStatus.Calculating) {
      refetchCalculation();
    }

    setCalculations({ ...calculations, status });
  }
}

interface CalculationStatusNotificationProps<T extends Calculation | Calculation[]> {
  projectId: string;
  calculations: T;
  setCalculations: (value: T) => void;
  refetchCalculation?: () => void;
}

export function CalculationStatusNotification<T extends Calculation | Calculation[]>({
  projectId,
  calculations,
  setCalculations,
  refetchCalculation,
}: CalculationStatusNotificationProps<T>): ReactElement {
  const apiUrl = getAppConfig().apiUrl.replace('/api', '');
  const notificationUrl = `${apiUrl}/project/${projectId}`;
  const connection = useRef<HubConnection>();
  useEffect(() => {
    connection.current = new HubConnectionBuilder()
      .withUrl(notificationUrl, {
        accessTokenFactory: getAuthorizationToken,
      })
      .withAutomaticReconnect()
      .build();

    // It's a nice to have connecting to the notifications, however, silently fail if it can't connect
    connection.current.start().catch(() => {});
    return () => {
      if (connection.current) {
        connection.current.stop();
      }
    };
  }, [notificationUrl]);

  const updateCalculationStatus = useCallback(
    ({ CalculationId, Status }: { CalculationId: string; Status: string }) => {
      console.info('Notification received', CalculationId, Status);
      updateCalculation(CalculationId, mapCompletionStatusToCalculationStatus(Status), calculations, setCalculations, refetchCalculation);
    },
    [calculations, setCalculations, refetchCalculation]
  );

  useEffect(() => {
    if (connection.current) {
      connection.current.off('UpdateCalcStatus');
      connection.current.on('UpdateCalcStatus', updateCalculationStatus);
    }
  }, [connection, updateCalculationStatus]);

  return <></>;
}
